import {_serializeSingle, _serializeExcept, _serializeExceptList, _serializeList, serializeHistoryChangeDetail} from './core';
import type {ServerData, ServerRecord} from './core';
import type {SerializedWithRaw} from './core';
import type {WithLabels, WithDepartment, WithDeletedParticipant, WithChanges} from './_extras';
import type {DomainOf} from '../../../types/domain';
import {serializeMemberBff, serializeLabelList, serializeDepartment, serializeDeletedParticipant} from './member';

// Document Template Folder
/** 스키마 밖에서 조립되는 관계 필드 — 서버 응답 계약의 일부. */
type DocumentTemplateBffExtras = WithLabels & WithDepartment & WithDeletedParticipant & {
    lastModifier?: unknown;
    documentTemplateFolders?: DomainOf<"documentTemplateFolder">[];
};
type TemplateHistoryExtras = WithChanges;

export function serializeDocumentTemplateFolder(data: ServerData) {
    return _serializeSingle("documentTemplateFolder", data);
}

export function serializeDocumentTemplateFolderList(data: ServerData) {
    return _serializeList("documentTemplateFolder", data);
}

export function serializeDocumentTemplateFolderCounts(data: ServerData): Partial<SerializedWithRaw<"documentTemplateFolderCounts">> {
    if (!data) return {allCount: 0, counts: {}};
    return {
        ...data,
        ..._serializeSingle("documentTemplateFolderCounts", data),
    };
}

// Document Template
export function serializeDocumentTemplate(data: ServerData) {
    return _serializeSingle("documentTemplate", data);
}

/** expansion 응답이 싣지 않는 documentTemplate 필드 */
const EXPANSION_DOCUMENT_TEMPLATE_OMIT = [
    "externalId", "statusDate.deletedDate", "lastModifiedDate", "favorite",
] as const;

/** system 응답이 싣지 않는 documentTemplate 필드 */
const SYSTEM_DOCUMENT_TEMPLATE_OMIT = [
    "form", "serverForm", "enabledDraftEdit", "departmentId", "lastModifiedDate", "favorite",
] as const;

export function serializeDocumentTemplateSystem(data: ServerData) {
    return _serializeExcept("documentTemplate", data, SYSTEM_DOCUMENT_TEMPLATE_OMIT);
}

export function serializeDocumentTemplateExpansion(data: ServerData) {
    return _serializeExcept("documentTemplate", data, EXPANSION_DOCUMENT_TEMPLATE_OMIT);
}

// Document Template BFF

/** admin BFF 응답이 싣지 않는 필드 */
const ADMIN_TEMPLATE_BFF_OMIT = ["favorite"] as const;

/** dept-head BFF 응답이 싣지 않는 필드 */
const HEAD_TEMPLATE_BFF_OMIT = ["favorite", "lastModifiedDate"] as const;

/** member BFF 응답이 싣지 않는 필드 */
const MEMBER_TEMPLATE_BFF_OMIT = ["lastModifiedDate"] as const;

/**
 * @param options.omit 역할별로 응답에 없는 스키마 필드
 */
export function serializeDocumentTemplateBff(data: ServerData, options: {omit?: readonly string[]} = {}) {
    if (!data) return null;
    const {omit} = options;
    const template: DomainOf<"documentTemplate"> & DocumentTemplateBffExtras = omit
        ? _serializeExcept("documentTemplate", data, omit) as DomainOf<"documentTemplate"> & DocumentTemplateBffExtras
        : _serializeSingle("documentTemplate", data);
    if (data.lastModifiedDate !== undefined) {
        template['documentTemplate.lastModifiedDate'] = data.lastModifiedDate || null;
    }
    // admin BFF: 최종 수정자 멤버(아바타/직책/부서 포함) — ColumnDocParticipant 표시용
    if (data.lastModifier) {
        template.lastModifier = serializeMemberBff(data.lastModifier);
    }
    if (data.labels) template.labels = serializeLabelList(data.labels);
    // 삭제자 스냅샷
    if (data.deletedParticipant) {
        template.deletedParticipant = serializeDeletedParticipant(data.deletedParticipant);
    }
    if (data.documentTemplateFolders) {
        template.documentTemplateFolders = serializeDocumentTemplateFolderList(data.documentTemplateFolders);
    }
    // 템플릿 귀속 부서(DEPARTMENT owner) — '관리 부서 전체' 통합 목록의 행별 부서 표시용
    if (data.department) {
        template.department = serializeDepartment(data.department);
    }
    // favorite 는 domain 정의의 documentTemplate.favorite 으로 _serializeSingle 에서
    // 이미 "documentTemplate.favorite" 키로 생성됨. 별도 unprefixed 추가 불필요.
    return template;
}

/** 목록·검색 응답이 싣지 않는 필드 — 본문 form 은 상세에만 */
const TEMPLATE_SUMMARY_OMIT = ["form"] as const;
const ADMIN_TEMPLATE_BFF_SUMMARY_OMIT = ["favorite", "form"] as const;
const HEAD_TEMPLATE_BFF_SUMMARY_OMIT = ["favorite", "lastModifiedDate", "form"] as const;
const MEMBER_TEMPLATE_BFF_SUMMARY_OMIT = ["lastModifiedDate", "form"] as const;

export function serializeDocumentTemplateSummary(data: ServerData) {
    return _serializeExcept("documentTemplate", data, TEMPLATE_SUMMARY_OMIT);
}

export function serializeDocumentTemplateSummaryList(data: ServerData) {
    return _serializeExceptList("documentTemplate", data, TEMPLATE_SUMMARY_OMIT);
}

export function serializeDocumentTemplateBffAdminSummary(data: ServerData) {
    return serializeDocumentTemplateBff(data, {omit: ADMIN_TEMPLATE_BFF_SUMMARY_OMIT});
}

export function serializeDocumentTemplateBffAdminSummaryList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeDocumentTemplateBffAdminSummary(item));
}

export function serializeDocumentTemplateBffHeadSummary(data: ServerData) {
    return serializeDocumentTemplateBff(data, {omit: HEAD_TEMPLATE_BFF_SUMMARY_OMIT});
}

export function serializeDocumentTemplateBffHeadSummaryList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeDocumentTemplateBffHeadSummary(item));
}

export function serializeDocumentTemplateBffMemberSummary(data: ServerData) {
    return serializeDocumentTemplateBff(data, {omit: MEMBER_TEMPLATE_BFF_SUMMARY_OMIT});
}

export function serializeDocumentTemplateBffMemberSummaryList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeDocumentTemplateBffMemberSummary(item));
}

export function serializeDocumentTemplateBffAdmin(data: ServerData) {
    return serializeDocumentTemplateBff(data, {omit: ADMIN_TEMPLATE_BFF_OMIT});
}

export function serializeDocumentTemplateBffHead(data: ServerData) {
    return serializeDocumentTemplateBff(data, {omit: HEAD_TEMPLATE_BFF_OMIT});
}

export function serializeDocumentTemplateBffMember(data: ServerData) {
    return serializeDocumentTemplateBff(data, {omit: MEMBER_TEMPLATE_BFF_OMIT});
}

export function serializeDocumentTemplateBffAdminList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeDocumentTemplateBffAdmin(item));
}

// Document Template History
/** 이력 응답이 싣지 않는 필드 */
const DOCUMENT_TEMPLATE_HISTORY_OMIT = ["workspaceId"] as const;

export function serializeDocumentTemplateHistory(data: ServerData) {
    if (!data) return null;
    const history = _serializeExcept("documentTemplateHistory", data, DOCUMENT_TEMPLATE_HISTORY_OMIT) as
        DomainOf<"documentTemplateHistory"> & TemplateHistoryExtras;
    if (Array.isArray(data.changes)) {
        const serializedChanges = data.changes.map((item: ServerRecord) => ({
            ...serializeHistoryChangeDetail(item),
            ..._serializeSingle("documentTemplateHistoryDetail", item),
        }));
        history.changes = serializedChanges;
    }
    return history;
}

export function serializeDocumentTemplateHistoryList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeDocumentTemplateHistory(item));
}
